import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSolana } from '../context/WalletContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowDownUp, Coins, Clock, CheckCircle, XCircle, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { icon: Clock, color: 'text-amber-400', bg: 'bg-amber-400/10', border: 'border-amber-400/20', label: 'Pending' },
  accepted: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-400/10', border: 'border-emerald-400/20', label: 'Accepted' },
  declined: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-400/20', label: 'Declined' },
};

export default function SwapPage() {
  const { user, token, refreshUser } = useAuth();
  const { connected } = useSolana();
  const [coinAmount, setCoinAmount] = useState('');
  const [rate, setRate] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [rateRes, reqRes] = await Promise.all([
        axios.get(`${API}/api/swap/rate`, { headers }),
        axios.get(`${API}/api/swap/my-requests`, { headers }),
      ]);
      setRate(rateRes.data);
      setRequests(reqRes.data);
    } catch (err) {
      console.error('Fetch swap data failed:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const coins = parseFloat(coinAmount) || 0;
  const grossSol = rate ? coins / rate.sol_to_coin_rate : 0;
  const feeSol = grossSol * (rate ? rate.fee_percent / 100 : 0.1);
  const netSol = grossSol - feeSol;

  const handleSubmit = async () => {
    if (coins <= 0) return toast.error('Enter a valid amount');
    if (coins > (user?.coins || 0)) return toast.error('Insufficient coins');
    if (!user?.wallet_address) return toast.error('No wallet connected');

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/swap/request`, { coin_amount: coins }, { headers });
      toast.success(res.data.message);
      setCoinAmount('');
      await refreshUser();
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Swap request failed');
    } finally {
      setSubmitting(false);
    }
  };

  const setPercent = (pct) => {
    const max = user?.coins || 0;
    setCoinAmount(String(Math.floor(max * pct)));
  };

  if (loading && !rate) {
    return (
      <div className="min-h-screen bg-[#FFFDF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFDF7] pb-24" data-testid="swap-page">
      {/* Header */}
      <div className="bg-gradient-to-b from-[#0c2942] to-transparent px-4 pt-6 pb-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-amber-600/20 flex items-center justify-center">
            <ArrowDownUp className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h1 className="text-white text-lg font-bold" style={{ fontFamily: "'Chakra Petch', sans-serif" }}>Swap Coins</h1>
            <p className="text-amber-300/50 text-xs">Convert your coins to SOL</p>
          </div>
        </div>
      </div>

      <div className="px-4 space-y-4 max-w-lg mx-auto">
        {/* Swap Card */}
        <div className="rounded-2xl border border-amber-800/30 bg-[#0c2038] p-5" data-testid="swap-form">
          {/* From: Coins */}
          <div className="mb-4">
            <label className="text-amber-400/60 text-xs font-medium mb-2 block">You Send</label>
            <div className="flex items-center gap-3 bg-[#091a2e] rounded-xl p-3 border border-amber-900/30">
              <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <Coins className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1">
                <input
                  type="number"
                  value={coinAmount}
                  onChange={(e) => setCoinAmount(e.target.value)}
                  placeholder="0"
                  className="w-full bg-transparent text-white text-xl font-bold outline-none placeholder:text-white/20"
                  data-testid="swap-coin-input"
                />
                <p className="text-amber-400/40 text-[10px]">
                  Balance: {(user?.coins || 0).toLocaleString()} Coins
                </p>
              </div>
              <span className="text-amber-300/60 text-sm font-medium">COINS</span>
            </div>
            {/* Quick percent buttons */}
            <div className="flex gap-2 mt-2">
              {[0.25, 0.5, 0.75, 1].map((pct) => (
                <button
                  key={pct}
                  onClick={() => setPercent(pct)}
                  className="flex-1 text-[10px] py-1.5 rounded-lg bg-amber-900/30 text-amber-400/70 hover:bg-amber-800/40 hover:text-amber-300 transition-colors font-medium"
                >
                  {pct * 100}%
                </button>
              ))}
            </div>
          </div>

          {/* Arrow */}
          <div className="flex justify-center my-2">
            <div className="w-8 h-8 rounded-full bg-amber-600/20 flex items-center justify-center">
              <ArrowDownUp className="w-4 h-4 text-amber-400" />
            </div>
          </div>

          {/* To: SOL */}
          <div className="mb-4">
            <label className="text-amber-400/60 text-xs font-medium mb-2 block">You Receive</label>
            <div className="flex items-center gap-3 bg-[#091a2e] rounded-xl p-3 border border-amber-900/30">
              <div className="w-9 h-9 rounded-lg bg-black/40 flex items-center justify-center flex-shrink-0 p-1">
                <img src="/assets/solana-logo.png" alt="SOL" className="w-full h-full object-contain" />
              </div>
              <div className="flex-1">
                <p className="text-white text-xl font-bold" data-testid="swap-sol-output">
                  {netSol > 0 ? netSol.toFixed(6) : '0.000000'}
                </p>
                <p className="text-amber-400/40 text-[10px]">
                  To: {user?.wallet_address ? `${user.wallet_address.slice(0, 6)}...${user.wallet_address.slice(-4)}` : 'No wallet'}
                </p>
              </div>
              <span className="text-amber-300/60 text-sm font-medium">SOL</span>
            </div>
          </div>

          {/* Rate Info */}
          {rate && coins > 0 && (
            <div className="bg-[#091a2e] rounded-xl p-3 border border-amber-900/20 mb-4 space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-amber-400/50">Rate</span>
                <span className="text-amber-200/70">1 SOL = {rate.sol_to_coin_rate.toLocaleString()} Coins</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-amber-400/50">Gross</span>
                <span className="text-amber-200/70">{grossSol.toFixed(6)} SOL</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-amber-400/50">Fee ({rate.fee_percent}%)</span>
                <span className="text-red-400/70">-{feeSol.toFixed(6)} SOL</span>
              </div>
              <div className="border-t border-amber-900/20 pt-1.5 flex justify-between text-xs font-bold">
                <span className="text-amber-300">You Receive</span>
                <span className="text-emerald-400">{netSol.toFixed(6)} SOL</span>
              </div>
            </div>
          )}

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={submitting || coins <= 0 || coins > (user?.coins || 0) || !user?.wallet_address}
            className="w-full py-3 rounded-xl font-bold text-sm transition-all bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-amber-900/30"
            data-testid="swap-submit-btn"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Processing...</span>
            ) : !user?.wallet_address ? (
              'Connect Wallet First'
            ) : (
              'Submit Swap Request'
            )}
          </button>

          <p className="text-center text-amber-600/40 text-[10px] mt-2">
            Swap requires admin approval. SOL will be sent to your connected wallet.
          </p>
        </div>

        {/* My Swap History */}
        <div>
          <h2 className="text-amber-200/80 text-sm font-bold mb-3" style={{ fontFamily: "'Chakra Petch', sans-serif" }}>
            Swap History
          </h2>
          {requests.length === 0 ? (
            <div className="text-center py-8 text-amber-600/40 text-sm">
              No swap requests yet
            </div>
          ) : (
            <div className="space-y-2">
              <AnimatePresence>
                {requests.map((swap) => {
                  const cfg = statusConfig[swap.status] || statusConfig.pending;
                  const Icon = cfg.icon;
                  return (
                    <motion.div
                      key={swap.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`rounded-xl border ${cfg.border} ${cfg.bg} p-3`}
                      data-testid={`swap-item-${swap.status}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${cfg.color}`} />
                          <div>
                            <p className="text-white text-sm font-bold">
                              {swap.coin_amount.toLocaleString()} Coins
                            </p>
                            <p className="text-amber-400/50 text-[10px]">
                              {new Date(swap.created_at).toLocaleDateString()} · {new Date(swap.created_at).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-emerald-400 text-sm font-bold">{swap.net_sol?.toFixed(6)} SOL</p>
                          <span className={`text-[10px] font-medium ${cfg.color}`}>{cfg.label}</span>
                        </div>
                      </div>
                      {swap.tx_signature && (
                        <a
                          href={`https://solscan.io/tx/${swap.tx_signature}${swap.network === 'devnet' ? '?cluster=devnet' : ''}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 mt-2 text-amber-500 text-[10px] hover:text-amber-300"
                        >
                          <ExternalLink className="w-3 h-3" /> View Transaction
                        </a>
                      )}
                      {swap.status === 'declined' && swap.admin_note && (
                        <p className="mt-1 text-red-400/70 text-[10px]">
                          <AlertCircle className="w-3 h-3 inline mr-1" />{swap.admin_note}
                        </p>
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
